import { z } from 'zod';

export type AppEnvT = 'development' | 'qa' | 'sandbox' | 'production';

export type FirebaseEnvT = Readonly<{
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}>;

export type WebEnvT<TExtra extends Record<string, unknown> = Record<string, never>> = Readonly<{
  appEnv: AppEnvT;
  apiBaseUrl: string;
  firebase: FirebaseEnvT;
  isDevelopment: boolean;
  isProduction: boolean;
  extra: TExtra;
}>;

export type WebEnvRawT = z.infer<ReturnType<typeof buildEnvSchema>>;

export type BuildEnvSchemaOptionsT<TExtraShape extends z.ZodRawShape = Record<string, never>> = {
  extra?: TExtraShape;
};

const baseEnvShape = {
  VITE_APP_ENV: z.enum(['development', 'qa', 'sandbox', 'production']).default('development'),
  VITE_API_BASE: z.string().url(),
  VITE_FIREBASE_API_KEY: z.string().min(1),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1),
  VITE_FIREBASE_STORAGE_BUCKET: z.string().min(1),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().min(1),
  VITE_FIREBASE_APP_ID: z.string().min(1),
} satisfies z.ZodRawShape;

export function buildEnvSchema<TExtraShape extends z.ZodRawShape = Record<string, never>>(
  options: BuildEnvSchemaOptionsT<TExtraShape> = {}
) {
  return z.object({
    ...baseEnvShape,
    ...(options.extra ?? {}),
  });
}

export function parseWebEnv<TExtraShape extends z.ZodRawShape = Record<string, never>>(
  source: Record<string, unknown>,
  options: BuildEnvSchemaOptionsT<TExtraShape> = {}
): WebEnvT<z.infer<z.ZodObject<TExtraShape>>> {
  const schema = buildEnvSchema(options);
  const result = schema.safeParse(source);

  if (!result.success) {
    const formatted = result.error.issues
      .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n');

    throw new Error(`Environment validation failed:\n${formatted}`);
  }

  const parsedEnv = result.data;
  const extraKeys = Object.keys(options.extra ?? {});
  const extra = Object.fromEntries(
    extraKeys.map((key) => [key, parsedEnv[key as keyof typeof parsedEnv]])
  ) as z.infer<z.ZodObject<TExtraShape>>;

  return {
    appEnv: parsedEnv.VITE_APP_ENV,
    apiBaseUrl: parsedEnv.VITE_API_BASE,
    firebase: {
      apiKey: parsedEnv.VITE_FIREBASE_API_KEY,
      authDomain: parsedEnv.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: parsedEnv.VITE_FIREBASE_PROJECT_ID,
      storageBucket: parsedEnv.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: parsedEnv.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: parsedEnv.VITE_FIREBASE_APP_ID,
    },
    isDevelopment: parsedEnv.VITE_APP_ENV === 'development',
    isProduction: parsedEnv.VITE_APP_ENV === 'production',
    extra,
  } as const;
}
